import React, {useContext} from "react";
import {PrepareSubmissionParams, useSubmissionPrepare} from "./useSubmissionPrepare";
import {Fields} from "../../components/Fields";
import Input from "../../components/Input";
import {SelectString} from "../../components/SelectString";
import Dialog from "../../components/Dialog";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import FileUploader from "../../components/FileUploader";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import SelectNumber from "../../components/SelectNumber";
import {DatePicker, DatePickerType} from "../../components/DatePicker";
import AppContext from "../../appContext";
import Lab from "../../controllers/Lab";
import type {AxiosResponse} from "axios";
import type ReportDataRequest from "../../controllers/ReportDataRequest";
import {useStateAjax} from "../../wrapper";
import AgentController from "../../controllers/AgentController";
import DepotController from "../../controllers/DepotController";
import SelectNumberNullable from "../../components/SelectNumberNullable";
import {parseCSVFile} from "../CSVParse";

export interface RenderTableStruct
{
    render: React.ReactNode,
    validation: () => boolean
}

export interface SubmissionPrepareBaseProps<TSample> {
    emptyRow: () => TSample;
    testDataIds: number[];
    close: (saved: boolean) => void;

    prepare: (params: PrepareSubmissionParams) => Promise<AxiosResponse<SubmissionDataResponse<TSample>>>;
    submit: (request: ReportDataRequest<TSample>) => Promise<AxiosResponse<ReportDataRequest<TSample>>>;
    
    // Available lab
    labs: Lab[];

    // Additional differences can be provided here:
    csvPopulateMap: Record<string, (value: string, sample: TSample) => void>;
    renderTable: ( // function that renders the table(s)
        report: SubmissionDataResponse<TSample>,
        data: TSample[][],
        onDataChange: (newData: TSample[][]) => void,
        setShowUpload : (show: {show: boolean, index: number}) => void
    ) => RenderTableStruct;
}

export function SubmissionPrepareBase<TSample>(props: SubmissionPrepareBaseProps<TSample>) {
    const context = useContext(AppContext);
    const [agents,] = useStateAjax(AgentController.index, []);
    const [depots,] = useStateAjax(DepotController.index, []);

    const {
        isLoading,
        submissionData,
        updateHeader,
        splitData,
        setSplitData,
        handleSplit,
        handleCsvUpload,
        handleSubmit,
        validation,
    } = useSubmissionPrepare<TSample>(
        {
            prepareFn: props.prepare,
            submitFn: props.submit,
            csvPopulateMap: props.csvPopulateMap,
        },
        props.testDataIds
    );

    
    const warningState = useWarningState(null);
    const [showUpload, setShowUpload] = React.useState<{ show: boolean; index: number }>({
        show: false,
        index: -1,
    });

    if (isLoading) {
        return (
            <div className="absolute inset-0 bg-overlay-200 flex justify-center items-center">
                <img src="/images/loader.gif" alt=""/>
            </div>
        );
    }

    if (!submissionData) {
        return null;
    }

    // parse out from submissionData
    const dataHeader = submissionData.data;

    function closeUpload() {
        setShowUpload({
            show: false,
            index: -1
        })
    }
    
    function onCsvFileSelected(file: File, index: number) {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text: string = e.target?.result as string
            const parsed = parseCSVFile(text, props.emptyRow, props.csvPopulateMap);
            handleCsvUpload(index, parsed)
            closeUpload()
        };
        reader.readAsText(file);
    }

    const renderTable = props.renderTable(submissionData, splitData, setSplitData, setShowUpload);
    
    function showWarningIfValidationPassed() {
        // check your custom validations 
        if (!validation.validate() || !renderTable.validation()) {
            return;
        }
        warningState.show("Are you sure you want to submit this submission?", null);
    }


    return (
        <div className="p-4 text-left">
            <Fields
                columns={2}
                fields={[
                    {
                        label: "Entity Name",
                        value: (
                            <Input
                                value={dataHeader.clientName}
                                change={(v) => updateHeader({clientName: v})}
                                className={!validation.rules.entity ? "border border-red-500" : ""}
                            />
                        ),
                    },
                    {
                        label: "Agent",
                        value: (
                            <SelectNumberNullable options={agents} textFunc={r => r.name ?? ''}
                                          valueFunc={r => r.id} value={dataHeader.agentId}
                                          onChange={v => updateHeader({agentId: v ?? undefined})}
                                          className={!validation.rules.agent ? "border border-red-500" : ""}

                            />
                        ),
                    },
                    {
                        label: "Farm Name",
                        value: (
                            <Input
                                value={dataHeader.farmName}
                                change={(v) => updateHeader({farmName: v})}
                                className={!validation.rules.farm ? "border border-red-500" : ""}
                            />
                        ),
                    },
                    {
                        label: 'Language',
                        value: <SelectNumber options={context.initial.languages}
                                             textFunc={r => r.name ?? ''} valueFunc={r => r.id}
                                             value={dataHeader.languageId}
                                             onChange={v => updateHeader({languageId: v})}/>
                    },
                    {
                        label: 'Agronomist',
                        value: <SelectNumber options={submissionData.agronomists} textFunc={r => r.name ?? ''}
                                             valueFunc={r => r.id} value={dataHeader.agronomistId}
                                             onChange={v => updateHeader({agronomistId: v})}
                                             className={!validation.rules.agronomist ? "border border-red-500" : ""}
                        />
                    },
                    {
                        label: 'Sent on',
                        value: <DatePicker value={dataHeader.dateSent} setValue={d => updateHeader({dateSent: d})}
                                           type={DatePickerType.DateUtc}/>
                    },
                    {
                        label: 'Depot',
                        value: <SelectNumberNullable options={depots} textFunc={r => r.name ?? ''}
                                             valueFunc={r => r.id} value={dataHeader.depotId}
                                             onChange={v => updateHeader({depotId: v ?? undefined})}
                                             className={!validation.rules.depot ? "border border-red-500" : ""}
                        />
                    },
                    {
                        label: 'Date Sampled',
                        value: <DatePicker value={dataHeader.dateSampled}
                                           setValue={d => updateHeader({dateSampled: d})}
                                           type={DatePickerType.DateUtc}/>
                    }
                    ,
                    {
                        label: 'Submission Lab',
                        value: <SelectString value={dataHeader.lab} onChange={v => updateHeader({lab: v as Lab})}
                                             options={props.labs.reduce((acc, lab) => {
                                                 acc[lab] = lab;
                                                 return acc;
                                             }, {} as Record<string, string>)}/>
                    }
                ]}
            />

            {/* Now render the dynamic table(s). */}
            {renderTable.render}

            <div className="flex justify-end items-center p-2 border-t sticky bottom-0 bg-white">
                {!validation.rules ? (
                    <div className="text-xs text-red-500 px-2">
                        (each submission must include a minimum of one active row)
                    </div>
                ) : null}

                <div className="btn bg-red-500" onClick={() => props.close(false)}>
                    Cancel
                </div>
                <div className="btn bg-primary-500" onClick={showWarningIfValidationPassed}>
                    Submit
                </div>
            </div>

            <Dialog
                title="Upload CSV"
                body={
                    <div className="p-4">
                        <div>Download CSV Sample, add data and upload again.</div>
                        <a className="underline" href="/SubmissionSampleFarmersSample.csv" download="SubmissionSampleFarmersSample.csv">
                            SubmissionSampleFarmersSample.csv
                        </a>
                        <div className="my-2">
                            <FileUploader onChange={file => onCsvFileSelected(file, showUpload.index)} fileTypes=".csv"/>
                        </div>
                    </div>
                }
                show={showUpload.show}
                setShow={() => setShowUpload({show: false, index: -1})}
            />

            <WarningPopup state={warningState} onYes={() => {
                handleSubmit()
                    .then(_ => {
                        props.close(false)
                    })
            }}/>
        </div>
    );
}